import type { HelpCategory } from "../../types";

export const staff: HelpCategory = {
  id: "staff",
  title: "Персонал и зарплата",
  description: "Пользователи и роли, кадры и должности, рабочие смены, зарплата, чаевые и личный кабинет.",
  icon: "user-cog",
  articles: [
    {
      id: "users-roles",
      title: "Пользователи и роли: кто что может",
      summary: "Как добавить сотрудника с входом в программу, выдать роль в нужных филиалах, настроить права и лимиты без подтверждения.",
      path: "/staff/users",
      permission: "users.view",
      keywords: ["пользователь", "сотрудник", "роль", "права", "доступ", "пароль", "лимит", "курьер", "кассир", "официант"],
      related: ["employees-positions", "pos-discounts", "security"],
      body: [
        { t: "video", id: "staff", title: "Сотрудник с PIN-кодом", duration: 30 },
        {
          t: "terms", title: "Стандартные роли",
          items: [
            { term: "Владелец / Администратор компании", text: "Всё, во всех филиалах." },
            { term: "Управляющий филиалом", text: "Касса, меню, склад, персонал, отчёты своего филиала." },
            { term: "Бухгалтер", text: "Расходы, зарплата, отчёты, оплаты поставщикам." },
            { term: "Кассир, Официант", text: "Касса, заказы, брони, клиенты. Кассир ещё открывает и закрывает смену." },
            { term: "Повар", text: "Экран кухни, стоп-лист, техкарты (просмотр), производство." },
            { term: "Курьер", text: "Только свои доставки." },
            { term: "Аудитор", text: "Только просмотр всего." },
          ],
        },
        {
          t: "steps", title: "Добавить пользователя",
          items: [
            "[«Пользователи»](/staff/users) → **«Добавить сотрудника»**: имя, email или телефон, пароль (или приглашение). Указали email — нажмите «Отправить код»: код придёт сотруднику на почту, он называет его вам, вы вводите в «Код из письма сотруднику». То же при смене email.",
            "Роль и **филиалы**: роль может действовать во всех филиалах или только в выбранных.",
            "Сотрудник входит со своим паролем; на терминалах — по PIN (задаётся в кадрах).",
          ],
        },
        {
          t: "steps", title: "Своя роль",
          items: [
            "[«Роли»](/staff/roles) → **«Новая роль»**: отметьте права по разделам (просмотр, создание, изменение, удаление, особые действия).",
            "**Лимиты без подтверждения**: скидка %, сумма возврата, списания, изъятия. Выше лимита — PIN менеджера. См. [[pos-discounts]].",
            "Нельзя выдать роль с правами шире ваших собственных.",
          ],
        },
        { t: "warn", text: "Число пользователей с паролем ограничено тарифом. Сотрудники, входящие только по PIN на терминале, в лимит не входят." },
      ],
    },
    {
      id: "employees-positions",
      title: "Кадры и должности: схема оплаты, PIN, увольнение",
      summary: "Должности со схемой оплаты по умолчанию, карточка сотрудника с датой приёма, PIN для терминала и своей схемой оплаты.",
      path: "/staff/employees",
      feature: "core.staff",
      permission: "employees.view",
      keywords: ["кадры", "должность", "приём", "увольнение", "схема оплаты", "ставка", "оклад", "pin"],
      related: ["payroll", "work-shifts", "terminals"],
      body: [
        {
          t: "steps", title: "Должности",
          items: ["[«Должности»](/staff/positions): официант, повар, кассир…", "Схема оплаты по умолчанию — её получат сотрудники должности."],
        },
        {
          t: "terms", title: "Схемы оплаты",
          items: [
            { term: "Почасовая", text: "Ставка × отработанные часы." },
            { term: "За смену", text: "Фиксированная сумма за каждую смену." },
            { term: "Оклад в месяц", text: "Делится пропорционально отработанным дням периода." },
            { term: "Ночные", text: "Повышенный коэффициент за часы ночью (расширенная зарплата)." },
            { term: "% от личных продаж", text: "Процент от продаж сотрудника, можно ступенями (расширенная зарплата)." },
          ],
        },
        {
          t: "steps", title: "Сотрудник",
          items: [
            "[«Кадры»](/staff/employees): пользователь, должность, дата приёма, **своя схема оплаты** (если отличается от должности).",
            "**PIN** — для входа и отметки смены на терминале.",
            "**Уволить** — вход блокируется, история и расчёты сохраняются. Можно восстановить.",
          ],
        },
      ],
    },
    {
      id: "work-shifts",
      title: "Рабочие смены и табель",
      summary: "Приход и уход по PIN на терминале, из личного кабинета или вручную; перерывы, ночные часы, корректировки и утверждение табеля.",
      path: "/staff/shifts",
      feature: "core.staff",
      permission: "work_shifts.view",
      keywords: ["смена", "табель", "приход", "уход", "отметка", "часы", "перерыв", "опоздание"],
      related: ["payroll", "terminals", "my-work"],
      body: [
        {
          t: "list", title: "Как отмечаться",
          items: [
            "**На терминале**: экран PIN → «Отметить смену» → PIN → «Пришёл» / «Ушёл».",
            "**Сам** — в [«Мой кабинет»](/me/work) → «Начать смену».",
            "**Вручную** — управляющий: «Смена вручную».",
          ],
        },
        {
          t: "list", title: "Что считается",
          items: ["Отработанные часы минус перерывы.", "Ночные минуты (для ночного коэффициента).", "Незакрытая смена закрывается сама в момент начала нового бизнес-дня — её стоит проверить."],
        },
        { t: "p", text: "**Корректировка** — изменить время с причиной; исходное время сохраняется. **Утвердить** — смены попадают в расчёт зарплаты как подтверждённые." },
      ],
    },
    {
      id: "payroll",
      title: "Зарплата: расчёт, утверждение, выплата",
      summary: "Расчёт за период по схемам оплаты, бонусы, штрафы, авансы, утверждение ведомости и выплата из кассы или переводом.",
      path: "/staff/payroll",
      feature: "core.payroll_basic",
      permission: "payroll.view",
      keywords: ["зарплата", "ведомость", "аванс", "бонус", "штраф", "удержание", "выплата", "расчётный листок"],
      related: ["employees-positions", "work-shifts", "tips", "expenses-finance"],
      body: [
        {
          t: "steps",
          items: [
            "[«Зарплата»](/staff/payroll) → выберите период → **«Рассчитать»**. Система возьмёт смены, схемы оплаты и продажи.",
            "Добавьте начисления: **бонус**, **штраф**, **аванс**, **удержание** (часть — в расширенной зарплате).",
            "Проверьте ведомость по сотрудникам: часы, начислено, удержано, к выплате.",
            "**«Утвердить»** — ведомость фиксируется (v1, v2… при пересчётах до утверждения).",
            "**«Выплатить»**: из кассы (наличные уйдут из смены) или переводом.",
          ],
        },
        { t: "warn", text: "Утверждённая ведомость не меняется. Ошибку исправляют в следующем периоде; удержание, которое не поместилось в выплату, переносится дальше само." },
        { t: "tip", text: "Сотрудник видит свои расчётные листки в [«Моём кабинете»](/me/work). ФОТ попадает в финансовый отчёт — см. [[expenses-finance]]." },
      ],
    },
    {
      id: "tips",
      title: "Чаевые: распределение и выплата",
      summary: "Чаевые не выручка заведения. Их можно отдать официанту заказа, разделить поровну или по отработанным часам и выплатить из кассы.",
      path: "/staff/tips",
      feature: "core.pos",
      permission: "tips.view",
      keywords: ["чаевые", "tips", "котёл", "пул", "распределение"],
      related: ["pos-basics", "payroll"],
      body: [
        {
          t: "terms", title: "Способы распределения",
          items: [
            { term: "Официанту заказа", text: "Каждый получает чаевые со своих заказов." },
            { term: "Поровну (котёл)", text: "Все чаевые за период — поровну между участниками (расширенная зарплата)." },
            { term: "По часам (котёл)", text: "Пропорционально отработанным часам (расширенная зарплата)." },
          ],
        },
        { t: "steps", items: ["[«Чаевые»](/staff/tips) → период и способ → **«Распределить»**.", "Выплата — из кассы (уйдёт из смены) или вместе с зарплатой."] },
      ],
    },
    {
      id: "my-work",
      title: "Мой кабинет",
      summary: "Личная страница сотрудника: мои смены, продажи, чаевые и расчётные листки; начать и закончить смену.",
      path: "/me/work",
      keywords: ["мой кабинет", "мои смены", "моя зарплата", "листок"],
      related: ["work-shifts", "payroll", "tips"],
      body: [
        { t: "p", text: "[«Мой кабинет»](/me/work) доступен каждому сотруднику, без особых прав. Здесь видны свои смены и часы, продажи, чаевые и расчётные листки. Кнопка «Начать смену» отмечает приход без терминала." },
      ],
    },
    {
      id: "goals",
      title: "Цели и достижения",
      summary: "Цели на выручку, заказы, средний чек и продажи блюд — для заведения, филиала или сотрудника. Рейтинг команды, очки, уровни и достижения за работу.",
      path: "/staff/goals",
      feature: "core.staff",
      keywords: ["цели", "план продаж", "kpi", "достижения", "рейтинг", "мотивация", "очки", "уровень"],
      related: ["my-work", "work-shifts", "payroll"],
      body: [
        {
          t: "steps", title: "Поставить цель",
          items: [
            "[«Цели и достижения»](/staff/goals) → **«Поставить цель»**.",
            "Для кого: вся компания, филиал или сотрудник. Что считаем: выручка, заказы, средний чек, продано блюд (можно одно блюдо или категорию), новые клиенты.",
            "Период: день, неделя, месяц (повторяется сам) или свои даты. Личной цели можно дать очки и награду — например, премию.",
          ],
        },
        {
          t: "list", title: "Как считается",
          items: [
            "Прогресс — по оплаченным заказам за бизнес-день филиала; выручка — за вычетом возвратов.",
            "Заказ засчитывается официанту, а если его нет — тому, кто создал заказ.",
            "Средний чек засчитывается по итогу периода, остальные цели — как только достигнуты.",
            "Выполнение проверяется само раз в час; кнопка **«Проверить сейчас»** — сразу.",
          ],
        },
        { t: "p", text: "Достижения выдаются сами: первый заказ, 100 и 1000 заказов, большой стол, смены, касса без расхождений, новые клиенты, доставки, выполненные цели, лучший продавец недели. За каждое — очки (бронза 10, серебро 25, золото 50, платина 100), из очков складывается уровень." },
        { t: "tip", text: "Каждый сотрудник видит свои цели, уровень и достижения в [«Мои цели»](/me/goals) — без особых прав. О новом достижении и выполненной цели приходит уведомление." },
      ],
    },
  ],
};
